<template>
 <div class="chart chart-evolution">
    <vue-c3 :handler="handler"></vue-c3>
  </div>
</template>

<script>
import Vue from 'vue';
import VueC3 from 'vue-c3';
import dayjs from 'dayjs';
import Chroma from 'chroma-js';

require('c3/c3.css');

export default {
  components: {
    VueC3
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    range: {
      type: Array,
      default: () => []
    },
    singleAxis: {
      type: Boolean,
      default: true
    },
    grid: {
      type: Boolean,
      default: false
    },
    points: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: Date,
      default: () => new Date(0)
    }
  },
  data () {
    return {
      handler: new Vue(),
      zooming: undefined,
      columns: [],
      xs: {},
      names: {}
    };
  },
  watch: {
    data: {
      handler () {
        this.filterData();
        this.draw();
      },
      deep: true
    },
    range: {
      handler () {
        this.filterData();
        this.draw();
      }
    },
    singleAxis: {
      handler () {
        this.draw();
      }
    },
    grid: {
      handler () {
        this.draw();
      }
    },
    points: {
      handler () {
        this.draw();
      }
    }
  },
  mounted () {
    this.filterData();
    this.draw();
  },
  methods: {
    filterData () {
      const variables = this.data.variables;
      const startDate = this.data.startDate;
      const endDate = this.data.endDate;

      if (variables) {
        this.columns = [];

        for (let i = 0; i < variables.length; i++) {
          const variableData = variables[i].data.map(data => {
            const value = Number(data.value);

            // Filter by range if it is necessary
            if (this.range.length === 2 && value >= this.range[0] && value <= this.range[1]) {
              return value;
            } else if (this.range.length === 0) {
              return value;
            } else {
              return null;
            }
          });
          const timeData = variables[i].data.map(data => new Date(data.timestamp * 1000));

          // Null value at the end and at the begin to generate gaps
          variableData.unshift(null);
          variableData.push(null);
          timeData.unshift(startDate);
          timeData.push(endDate);

          this.columns.push(['data_' + i, ...variableData]);
          this.columns.push(['timestamp_' + i, ...timeData]);
          this.xs['data_' + i] = 'timestamp_' + i;
          this.names['data_' + i] = variables[i].metadata.label;
          if (variables[i].metadata.unit) {
            this.names['data_' + i] = this.names['data_' + i] + ' (' + variables[i].metadata.unit + ')';
          }
        }
      }
    },
    draw () {
      const mountedHeight = document.querySelector('.chart-evolution').offsetHeight;
      let variableCount = 0;
      if (this.data.variables) {
        variableCount = this.data.variables.length;
      }

      const options = {
        size: {
          height: mountedHeight
        },
        data: {
          xs: this.xs,
          columns: this.columns,
          names: this.names,
          axes: this.singleAxis ? {} : { data_0: 'y', data_1: 'y2' },
          type: this.points ? 'scatter' : 'line'
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              rotate: 75,
              culling: false,
              count: 50,
              format: '%Y-%m-%d %H:%M:%S'
            }
          },
          y2: {
            show: !this.singleAxis
          }
        },
        color: {
          pattern: generateColorScale(variableCount)
        },
        zoom: {
          enabled: true,
          extent: [-10, 10],
          onzoom: zoom => {
            onZoom(this, zoom);
          }
        },
        point: {
          show: false
        },
        line: {
          step: {
            type: 'step-after'
          }
        },
        grid: {
          y: {
            show: this.grid
          }
        }
      };
      this.handler.$emit('init', options);
      this.handler.$emit('dispatch', chart => {
        chart.zoom.max(new Date());
        chart.zoom.min(this.minDate);
      });
    }
  }
};

// -------------------------------------------------------------------------------------------------------------------
// Zoom

const onZoom = (context, zoom) => {
  if (context.zooming) {
    clearTimeout(context.zooming);
  }

  context.zooming = setTimeout(function (args) {
    const zoomMinDate = dayjs(zoom[0]).startOf('minute').toDate();
    const zoomMaxDate = dayjs(zoom[1]).startOf('minute').add(1, 'minute').toDate();
    zoom[0] = zoomMinDate < context.minDate ? context.minDate : zoomMinDate;
    zoom[1] = zoomMaxDate > new Date() ? new Date() : zoomMaxDate;

    context.$emit('zoom', zoom);
    context.zooming = undefined;
  }, 500);
};

function generateColorScale (numberOfColors) {
  if (numberOfColors === 0) {
    return ['#000000'];
  }
  return Chroma.scale('Set1').colors(numberOfColors);
}
</script>



// WEBPACK FOOTER //
// src/components/explorer/evolution/ChartEvolution.vue